import { useCallback, useEffect, useState } from "react";
import { BrowserRouter, Navigate, NavLink, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { api, session, type PublicUser } from "./api";
import { applyTheme, effective, readTheme, type Theme } from "./theme";
import {
  IconApprovals, IconBalances, IconDesk, IconMoon, IconOrders, IconOverview,
  IconPricing, IconRecon, IconSignOut, IconSun, IconUsers,
} from "./components/icons";
import { Mark, Wordmark } from "./components/brand";
import { Login } from "./pages/Login";
import { Enroll } from "./pages/Enroll";
import { Users } from "./pages/Users";
import { Balances } from "./pages/Balances";
import { Pricing } from "./pages/Pricing";
import { Orders } from "./pages/Orders";
import { Recon } from "./pages/Recon";
import { Approvals } from "./pages/Approvals";
import { DeskControl } from "./pages/DeskControl";
import { Overview } from "./pages/Overview";

type Role = PublicUser["role"];

interface NavEntry {
  to: string;
  label: string;
  icon: typeof IconOverview;
  roles?: Role[];
  group: string;
}

// One table drives the sidebar, the page titles and the route guards, so a new
// screen cannot appear in the nav without a route or be reachable by a role
// that is not allowed to see it — the two used to be maintained separately.
const NAV: NavEntry[] = [
  { to: "/", label: "Overview", icon: IconOverview, group: "Desk" },
  { to: "/orders", label: "Orders", icon: IconOrders, group: "Desk" },
  { to: "/balances", label: "Balances", icon: IconBalances, group: "Desk" },
  { to: "/recon", label: "Reconciliation", icon: IconRecon, group: "Desk" },
  { to: "/approvals", label: "Approvals", icon: IconApprovals, roles: ["admin"], group: "Control" },
  { to: "/pricing", label: "Pricing", icon: IconPricing, roles: ["admin"], group: "Control" },
  { to: "/users", label: "Users", icon: IconUsers, roles: ["admin"], group: "Control" },
  { to: "/desk-control", label: "Desk control", icon: IconDesk, roles: ["admin", "trader"], group: "Control" },
];

const TITLES: Record<string, string> = Object.fromEntries(NAV.map((n) => [n.to, n.label]));

function ThemeToggle() {
  const [theme, setTheme] = useState<Theme>(readTheme);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  // Cycles system → light → dark → system. The label always names what the
  // NEXT press does, so an icon-only control still says what it is to a
  // screen reader and to a tooltip.
  const next: Theme = theme === "system" ? "light" : theme === "light" ? "dark" : "system";
  const label = `Appearance: ${theme}. Switch to ${next}.`;

  return (
    <button
      type="button"
      className="btn btn-ghost btn-icon"
      onClick={() => setTheme(next)}
      aria-label={label}
      title={label}
    >
      {effective(theme) === "dark" ? <IconMoon /> : <IconSun />}
    </button>
  );
}

function Shell() {
  const [role, setRole] = useState<Role | null>(null);
  const [name, setName] = useState("");
  const [pending, setPending] = useState(0);
  // Desk control only exists when venue connectivity is simulated.
  //
  // This used to probe `/api/health` for a `mode` field. That route does not
  // exist — the only health endpoint is `/api/v1/health`, which is guarded by
  // the Xchange360 API key and answers 401 to a browser — so the check 404'd
  // on every load and the nav link never appeared even in simulation. Probing
  // `/api/sim` instead asks the endpoint the page actually depends on: it
  // answers 404 when simulation is off, which is exactly the condition being
  // tested. Gates the NAV LINK only; the route stays registered so a deep
  // link does not bounce to "/" while this resolves.
  const [simulated, setSimulated] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    if (!session.token) return;
    fetch("/api/sim", { headers: { authorization: `Bearer ${session.token}` } })
      .then((r) => setSimulated(r.ok))
      .catch(() => setSimulated(false));
  }, []);

  useEffect(() => {
    if (!session.token) {
      navigate("/login");
      return;
    }
    api
      .me()
      .then((me) => {
        if (me.scope !== "full") navigate("/enroll", { state: { mustSetupTotp: true } });
        else {
          setRole(me.role);
          setName(sessionStorage.getItem("otc.who") ?? me.user_id);
        }
      })
      .catch(() => {
        session.clear();
        navigate("/login");
      });
  }, [navigate]);

  // The approvals count in the nav. Anything waiting on a person is the one
  // thing worth interrupting for, so it is surfaced without needing the page
  // open — and stays silent at zero rather than showing a "0" badge.
  const loadPending = useCallback(() => {
    if (role !== "admin") return;
    fetch("/api/approvals", { headers: { authorization: `Bearer ${session.token ?? ""}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setPending(d.approvals.filter((a: { status: string }) => a.status === "pending").length))
      .catch(() => undefined);
  }, [role]);

  useEffect(() => {
    loadPending();
    const t = setInterval(loadPending, 30_000);
    return () => clearInterval(t);
  }, [loadPending, pathname]);

  async function logout() {
    await api.logout().catch(() => undefined);
    session.clear();
    navigate("/login");
  }

  if (!role) return null;

  const visible = NAV.filter(
    (n) => (!n.roles || n.roles.includes(role)) && (n.to !== "/desk-control" || simulated),
  );
  const groups = [...new Set(visible.map((n) => n.group))];
  const initials = (name || "?")
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("");

  return (
    <div className="app">
      <nav className="sidebar" aria-label="Main">
        <div className="brand">
          {/* The wordmark at full width, the X alone once the rail collapses —
              a 284-wide lockup squeezed into a 68px rail is illegible, and
              scaling it down to fit makes the whole product look cheap. */}
          <Wordmark height={17} className="brand-full" />
          <Mark height={24} className="brand-compact" />
        </div>

        {groups.map((g) => (
          <div key={g}>
            <div className="nav-group t-label">{g}</div>
            {visible
              .filter((n) => n.group === g)
              .map((n) => {
                const Icon = n.icon;
                return (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    end={n.to === "/"}
                    className={({ isActive }) => `nav-item${isActive ? " active" : ""}`}
                  >
                    <Icon />
                    <span>{n.label}</span>
                    {n.to === "/approvals" && pending > 0 && <span className="nav-count">{pending}</span>}
                  </NavLink>
                );
              })}
          </div>
        ))}

        <div className="sidebar-spacer" />

        <div className="sidebar-foot">
          <div className="who">
            <span className="avatar">{initials}</span>
            <span className="who-text" style={{ minWidth: 0 }}>
              <div style={{ fontSize: 12.5, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {name}
              </div>
              <div className="t-label" style={{ fontSize: 10 }}>{role}</div>
            </span>
          </div>
          <button type="button" className="nav-item" style={{ width: "100%", border: 0, background: "none" }} onClick={() => void logout()}>
            <IconSignOut />
            <span>Sign out</span>
          </button>
        </div>
      </nav>

      <div className="main">
        <header className="topbar">
          <h1>{TITLES[pathname] ?? "Overview"}</h1>
          <div className="topbar-right">
            <ThemeToggle />
          </div>
        </header>

        <div className="page">
          <Routes>
            <Route path="/" element={<Overview />} />
            <Route path="/orders" element={<Orders />} />
            <Route path="/balances" element={<Balances />} />
            <Route path="/recon" element={<Recon canAct={role === "admin" || role === "recon"} />} />
            {role === "admin" && <Route path="/approvals" element={<Approvals onChange={loadPending} />} />}
            {role === "admin" && <Route path="/pricing" element={<Pricing />} />}
            {role === "admin" && <Route path="/users" element={<Users />} />}
            {(role === "admin" || role === "trader") && <Route path="/desk-control" element={<DeskControl />} />}
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </div>
      </div>
    </div>
  );
}

export function App() {
  // Applied before first paint in main.tsx too; repeated here so a hot reload
  // or a direct mount cannot leave the document without a theme attribute.
  useEffect(() => {
    applyTheme(readTheme());
  }, []);

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/enroll" element={<Enroll />} />
        <Route path="/*" element={<Shell />} />
      </Routes>
    </BrowserRouter>
  );
}
